import React, { useState, useEffect, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import moment from "moment-timezone";
import * as actions from '../../../redux/actions/growthEngine';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { formatCampaignMoney, getCampaignMinBudget, getTimezoneLabel, getAccountTodayString, calendarDateFromString, isAccountCalendarToday, isBeforeAccountToday, toScheduleDateString, getMinStartTimeForDate, isScheduleStartTimeTooSoon, getAccountNowDisplay12h, formatScheduleTime12h, START_TIME_BUFFER_MINUTES } from "../../../helper/utility";

export const StepThree = () => {
    const dispatch = useDispatch();
    const { formData } = useSelector(state => state.growthEngine);
    const accountTimezone = formData.timezone || "";

    const minBudget = useMemo(() => getCampaignMinBudget({
        budgetType: formData.budgetType,
        minDailyBudget: formData.minDailyBudget,
        startDate: formData.startDate,
        startTime: formData.startTime,
        endDate: formData.endDate,
        endTime: formData.endTime,
    }), [
        formData.budgetType,
        formData.minDailyBudget,
        formData.startDate,
        formData.startTime,
        formData.endDate,
        formData.endTime,
    ]);

    const sliderMin = Math.max(1, Math.ceil(minBudget || 1));
    const sliderMax = Math.max(sliderMin * 20, sliderMin + 1000, 1000);
    const sliderStep = sliderMin >= 100 ? 10 : 1;
    const currencyLabel = formData.currency || "USD";
    const accountTodayStr = useMemo(
        () => getAccountTodayString(accountTimezone),
        [accountTimezone]
    );

    // Recompute min start time periodically so "now" stays accurate on account-today
    const [nowTick, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick((n) => n + 1), 30000);
        return () => clearInterval(id);
    }, []);

    const minStartTime = useMemo(
        () => getMinStartTimeForDate(formData.startDate, accountTimezone),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formData.startDate, accountTimezone, accountTodayStr, nowTick]
    );

    const startTimeTooSoon = useMemo(
        () => isScheduleStartTimeTooSoon(formData.startDate, formData.startTime, accountTimezone),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formData.startDate, formData.startTime, accountTimezone, nowTick]
    );

    const accountNowDisplay = useMemo(
        () => getAccountNowDisplay12h(accountTimezone),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountTimezone, nowTick]
    );

    useEffect(() => {
        const current = Number(formData.budget) || 0;
        if (minBudget > 0 && current < minBudget) {
            dispatch(actions.updateFormData({ budget: Math.ceil(minBudget) }));
        }
    }, [minBudget, formData.budget, dispatch]);

    // Keep selected start/end on/after account-today when timezone loads or day rolls over
    useEffect(() => {
        const todayDate = calendarDateFromString(accountTodayStr);
        if (!todayDate) return;

        const updates = {};
        const startStr = toScheduleDateString(formData.startDate);
        if (!startStr || startStr < accountTodayStr) {
            updates.startDate = todayDate;
        }
        const endStr = toScheduleDateString(formData.endDate);
        if (formData.endDate && endStr && endStr < accountTodayStr) {
            updates.endDate = null;
        }
        if (Object.keys(updates).length) {
            dispatch(actions.updateFormData(updates));
        }
    }, [accountTodayStr, accountTimezone, dispatch]);

    // On account-today, bump start time if it's already past in the ad account timezone
    useEffect(() => {
        if (!minStartTime || !formData.startTime) return;
        if (formData.startTime < minStartTime) {
            dispatch(actions.updateFormData({ startTime: minStartTime }));
        }
    }, [minStartTime, formData.startTime, dispatch]);

    const handleStartTimeChange = (value) => {
        if (minStartTime && value && value < minStartTime) {
            toast.warning(`Start time must be at least ${START_TIME_BUFFER_MINUTES} minutes from now in your ad account timezone`, {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true,
            });
            dispatch(actions.updateFormData({ startTime: minStartTime }));
            return;
        }
        dispatch(actions.updateFormData({ startTime: value }));
    };

    const handlePriceChange = (event) => {
        const next = Number(event.target.value);
        dispatch(actions.updateFormData({ budget: Math.max(next, sliderMin) }));
    };

    // Date range selection — month grid follows account-today when timezone is known
    const [currentMonth, setCurrentMonth] = useState(() => {
        return calendarDateFromString(getAccountTodayString("")) || new Date();
    });

    useEffect(() => {
        const todayDate = calendarDateFromString(accountTodayStr);
        if (todayDate) setCurrentMonth(todayDate);
    }, [accountTimezone]);

    // Get the start and end of the current month
    const startOfCurrentMonth = startOfMonth(currentMonth);
    const endOfCurrentMonth = endOfMonth(currentMonth);

    // Get all days of the current month
    const daysInMonth = eachDayOfInterval({
        start: startOfCurrentMonth,
        end: endOfCurrentMonth,
    });

    // Navigate to next and previous month
    const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    // Handle date range selection (calendar day = Meta ad account calendar day)
    const handleDateClick = (date) => {
        const selectedStr = toScheduleDateString(date);

        // Past relative to Meta ad account "today" is not allowed (account-today is allowed)
        if (!selectedStr || selectedStr < accountTodayStr) {
            toast.warning('Please select today or a future date in your ad account timezone', {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true
            })
            return;
        }

        const selectedDate = calendarDateFromString(selectedStr) || date;

        if (!formData?.startDate || (formData?.startDate && formData?.endDate)) {
            dispatch(actions.updateFormData({ startDate: selectedDate, endDate: null }));
        } else if (formData?.startDate && !formData?.endDate && selectedStr >= toScheduleDateString(formData.startDate)) {
            dispatch(actions.updateFormData({ endDate: selectedDate }));
        } else {
            dispatch(actions.updateFormData({ startDate: selectedDate, endDate: null }));
        }
    };

    // Determine if a date is within the selected range
    const isWithinRange = (date) => {
        if (formData?.startDate && formData?.endDate) {
            const day = toScheduleDateString(date);
            const startStr = toScheduleDateString(formData.startDate);
            const endStr = toScheduleDateString(formData.endDate);
            return day > startStr && day < endStr;
        }
        return false;
    };

    const isSelectedStart = (date) => toScheduleDateString(date) === toScheduleDateString(formData?.startDate);
    const isSelectedEnd = (date) => toScheduleDateString(date) === toScheduleDateString(formData?.endDate);

    const dateformater = (isoString) => {
        const date = new Date(isoString);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "long" });
        const year = date.getFullYear();

        // Add ordinal suffix (st, nd, rd, th)
        const ordinalSuffix = (d) => {
            if (d > 3 && d < 21) return "th";
            switch (d % 10) {
                case 1: return "st";
                case 2: return "nd";
                case 3: return "rd";
                default: return "th";
            }
        };

        return `${day}${ordinalSuffix(day)} ${month} ${year}`;
    }

    const isLessThan24HoursLocal = (startDate, startTime, endDate, endTime) => {
        if (!startDate || !startTime || !endDate || !endTime) return false;

        const startDay = toScheduleDateString(startDate);
        const endDay = toScheduleDateString(endDate);
        if (!startDay || !endDay) return false;

        const zone = accountTimezone || moment.tz.guess();
        const start = moment.tz(`${startDay}T${startTime}`, zone);
        const end = moment.tz(`${endDay}T${endTime}`, zone);
        if (!start.isValid() || !end.isValid()) return false;

        return end.diff(start, "hours", true) < 24;
    };

    const show24HrError = formData.budgetType === 'lifetime' && isLessThan24HoursLocal(
        formData.startDate,
        formData.startTime,
        formData.endDate,
        formData.endTime
    );

    const minBudgetCopy = formData.budgetType === 'lifetime'
        ? `Minimum lifetime budget for ${currencyLabel} is ${formatCampaignMoney(Math.ceil(minBudget || 0), formData.currency)} (based on Meta daily min × campaign days).`
        : `Minimum daily budget for ${currencyLabel} is ${formatCampaignMoney(Math.ceil(minBudget || formData.minDailyBudget || 0), formData.currency)}.`;

    return (
        <div className="font-inter text-theme_clr1 mb-[61px]" data-tour="budget-schedule">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Budget and campaign duration</h5>
                <div className="description text-white text-opacity-80 text-[15px]">Decide on the duration you want to run your ads for, and set your budget</div>
            </div>
            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5" data-tour="set-budget">
                    <div className="left-info lg:w-[50%]">
                        <div className="title-name text-[22.69px] font-medium mb-2">Set your budget</div>
                        <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                            Drag the slider to set your budget.  This will determine
                            the potential length of your campaign.
                        </div>
                    </div>
                    <div className="right-info lg:w-[50%]">
                        <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                            <div className="price-range mb-4">
                                <input
                                    type="range"
                                    min={sliderMin}
                                    max={sliderMax}
                                    step={sliderStep}
                                    value={Math.min(Math.max(Number(formData.budget) || sliderMin, sliderMin), sliderMax)}
                                    onChange={handlePriceChange}
                                    className="custom-range-slider w-full"
                                />
                                <div className="text-xs font-medium flex justify-between gap-2">
                                    <span>{formatCampaignMoney(formData.budget, formData.currency)}</span>
                                    <span className="text-slate-500">{formatCampaignMoney(sliderMax, formData.currency)} max</span>
                                </div>
                            </div>
                            <div className="range-description font-medium">
                                <div className="text-gray-600 font-medium text-xs mb-2">Choose your budget type -</div>
                                <div className="flex flex-wrap space-x-6 mb-4">
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input type="radio" name="budget_type" value="option2" className="form-radio"
                                            checked={formData.budgetType === "lifetime"}
                                            onChange={() => dispatch(actions.updateFormData({ budgetType: 'lifetime' }))}
                                        />
                                        <span className="ml-2 text-theme_clr1 font-medium">Lifetime Budget</span>
                                    </label>
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input type="radio" name="budget_type" value="option1" className="form-radio"
                                            checked={formData.budgetType === "daily"}
                                            onChange={() => dispatch(actions.updateFormData({ budgetType: 'daily' }))}
                                        />
                                        <span className="ml-2 text-theme_clr1 font-medium">Daily Budget</span>
                                    </label>
                                </div>
                                <div className="text-xs mb-3">
                                    {formData.minDailyBudget
                                        ? minBudgetCopy
                                        : `Budget amounts use your Meta ad account currency${formData.currency ? ` (${formData.currency})` : ""}.`}
                                </div>
                                <div className="text-xs font-semibold">
                                    NOTE: Amounts are in your Meta ad account currency
                                    {formData.currency ? ` (${formData.currency})` : ""}.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="border-black my-4" />

                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5" data-tour="set-dates">
                    <div className="left-info lg:w-[50%]">
                        <div className="title-name text-[22.69px] font-medium mb-2">Set your dates</div>
                        <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                            <div className="mb-3" >Decide how long you want to run your ads for.</div>
                            <div className="font-light mb-3"><span className="font-bold">Start Date:</span> Choose the date you want your campaign to launch.</div>
                            <div className="font-light mb-3"><span className="font-bold">End Date:</span> Choose the date you want your campaign to end.It can be optional for daily budget.</div>
                            {formData.timezone ? (
                                <div className="font-light text-sm text-slate-600 mt-1">
                                    Schedule times use your Meta ad account timezone:{" "}
                                    <span className="font-semibold text-theme_clr1">{getTimezoneLabel(formData.timezone)}</span>
                                </div>
                            ) : (
                                <div className="font-light text-sm text-slate-600 mt-1">
                                    Schedule times will use your Meta ad account timezone once the ad account is loaded.
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="right-info lg:w-[50%]">
                        <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                            <div className="calendar-wrapper max-w-[312px] w-full text-center mx-auto">
                                {(formData?.startDate && formData?.endDate) && (
                                    <div className="text-theme_clr1 text-[14.25px] font-medium mb-4">{format(formData?.startDate, "MMMM dd, yyyy")} - {format(formData?.endDate, "MMMM dd, yyyy")}</div>
                                )}
                                {/* Month Navigation */}
                                <div className="text-[12.8px] font-roboto font-medium flex items-center justify-between mb-2">
                                    <button className="h-[30px] w-[40px]" onClick={goToPreviousMonth}><i className="bi bi-chevron-left"></i></button>
                                    <span>{format(currentMonth, "MMMM yyyy")}</span>
                                    <button className="h-[30px] w-[40px]" onClick={goToNextMonth}><i className="bi bi-chevron-right"></i></button>
                                </div>
                                <div className="grid grid-cols-7 gap-[1px] text-center">
                                    {/* Days of the Week */}
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">S</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">M</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">T</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">W</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">T</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">F</div>
                                    <div className="text-xs font-roboto h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center">S</div>

                                    {/* Empty days before the first day of the month */}
                                    {Array.from({ length: startOfCurrentMonth.getDay() }).map((_, index) => (
                                        <div key={index} className="p-2"></div>
                                    ))}

                                    {/* Days of the month */}
                                    {daysInMonth.map((date) => {
                                        const isPast = isBeforeAccountToday(date, accountTimezone);
                                        return (
                                        <div key={date} onClick={() => handleDateClick(date)}
                                            className={`text-theme_clr1 text-sm font-inter font-medium h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center rounded-full cursor-pointer p-1
                                        ${isSelectedStart(date) ? "bg-gradient-to-r from-white from-[-15%] to-theme_clr1" : ""}
                                        ${isSelectedEnd(date) ? "bg-gradient-to-r from-white from-[-15%] to-theme_clr1" : ""}
                                        ${isWithinRange(date) ? "bg-blue-200" : ""}
                                        ${isAccountCalendarToday(date, accountTimezone) ? "bg-yellow-300" : ""}
                                        ${isPast ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-200"}`}
                                        >
                                            {format(date, "d")}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="border-black my-4" />
                <div data-tour="set-times">
                    <div className="flex flex-col lg:flex-row gap-4 xl:gap-5 pb-4">
                        <div className="left-info lg:w-[50%]">
                            <div className="title-name text-[22.69px] font-medium mb-2">Set your start time</div>
                            <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                                What time on the {dateformater(formData.startDate)} would you like
                                your campaign to start?
                            </div>
                        </div>
                        <div className="right-info lg:w-[50%]">
                            <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                <div className="input-wrapper">
                                    <input
                                        type="time"
                                        value={formData?.startTime}
                                        min={minStartTime || undefined}
                                        onChange={(e) => handleStartTimeChange(e.target.value)}
                                        placeholder="HH:MM"
                                        className="w-full bg-theme_clr1 text-white rounded-full px-4 py-2"
                                        required
                                        style={{ themeColor: "light" }}
                                    />
                                    <style>
                                        {`
                                    input[type="time"]::-webkit-calendar-picker-indicator {
                                    filter: invert(100%);
                                    }
                                `}
                                    </style>
                                </div>
                                {accountTimezone ? (
                                    <div className="text-theme_clr1 text-sm mt-2">
                                        Current time in your ad account
                                        {getTimezoneLabel(accountTimezone) ? ` (${getTimezoneLabel(accountTimezone)})` : ""}:{" "}
                                        <span className="font-semibold">{accountNowDisplay}</span>
                                    </div>
                                ) : null}
                                {minStartTime ? (
                                    <div className="text-theme_clr1 text-sm mt-1">
                                        Earliest start today:{" "}
                                        <span className="font-semibold">{formatScheduleTime12h(minStartTime)}</span>
                                        {" "}(at least {START_TIME_BUFFER_MINUTES} minutes from now)
                                    </div>
                                ) : null}
                                {startTimeTooSoon ? (
                                    <div className="text-red-500 text-sm mt-2">
                                        Start time is too soon. Pick {formatScheduleTime12h(minStartTime)} or later.
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <hr className="border-black my-4" />

                    <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                        <div className="left-info lg:w-[50%]">
                            <div className="title-name text-[22.69px] font-medium mb-2">Set your end time</div>
                            <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                                What time on the {formData.endDate === null ? dateformater(formData.startDate) : dateformater(formData.endDate)} would you like
                                your campaign to end?
                            </div>
                        </div>
                        <div className="right-info lg:w-[50%]">
                            <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                <div className="input-wrapper">
                                    <input type="time" value={formData?.endTime} onChange={(e) => dispatch(actions.updateFormData({ endTime: e.target.value }))} placeholder="HH:MM" className="w-full bg-theme_clr1 text-white rounded-full px-4 py-2" required style={{ themeColor: "light" }} />
                                    <style>
                                        {`
                                    input[type="time"]::-webkit-calendar-picker-indicator {
                                    filter: invert(100%);
                                    }
                                `}
                                    </style>
                                </div>
                            </div>
                            {show24HrError && (
                                <div className="text-red-500 text-sm mt-2">
                                    End date/time must be at least 24 hours after the start date/time for a lifetime budget.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
